import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShoppingCart, Droplets, Pill, Utensils, Shirt, PackageOpen, Car, Laptop, Heart, Home } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  ShoppingCart, Droplets, Pill, Utensils, Shirt, PackageOpen, Car, Laptop, Heart, Home
};

export default function IndustriesPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page/industries`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching industries content:", error);
      // Use default content on error
      setContent(getDefaultContent());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultContent = () => ({
    hero_title: "Industries We Serve",
    hero_subtitle: "Specialized POSM solutions tailored to the unique requirements of diverse industries, from FMCG giants to automotive leaders.",
    industries: [
      { icon: "ShoppingCart", title: "FMCG & Retail", description: "Custom displays for snacks, beverages, personal care, and household products. Serving brands like Lay's, Pepsi, Dabur, ITC, and P&G.", products: ["Floor Standing Units", "Counter Top Displays", "Promotional Standees"], clients: ["Lay's", "Pepsi", "ITC Limited", "Dabur", "P&G"], image: "" },
      { icon: "Droplets", title: "Beverages", description: "Cooler bins, cylindrical displays, and premium kiosks for beverage brands requiring visibility and accessibility.", products: ["Beverage Cooler Bins", "Cylindrical Displays", "Mobile Kiosks"], clients: ["Coca-Cola", "Pepsi", "Tropicana", "Red Bull", "Bisleri"], image: "" },
      { icon: "Pill", title: "Pharmaceutical", description: "Hygienic counter-top units and secured displays for OTC medications and health products with clear product information.", products: ["Secured CTU", "Information Standees", "Product Organizers"], clients: ["GSK", "Health & Wellness Brands"], image: "" },
      { icon: "Utensils", title: "Food & Confectionery", description: "Temperature-appropriate displays, tiered racks, and premium showcases for chocolates, snacks, and confectionery items.", products: ["Chocolate Displays", "Tiered Racks", "Premium Showcases"], clients: ["Ferrero Rocher", "Snickers", "Kit Kat", "Godiva"], image: "" },
      { icon: "Shirt", title: "Personal Care & Cosmetics", description: "Elegant displays with proper lighting and mirrored sections for skincare, haircare, and grooming products.", products: ["Illuminated Displays", "Mirror Units", "Tester Stations"], clients: ["Gillette", "Schwarzkopf", "Nivea", "Pantene"], image: "" },
      { icon: "PackageOpen", title: "Automotive & Industrial", description: "Heavy-duty displays for tires, oils, and automotive accessories with product specification charts and comparison features.", products: ["Tire Displays", "Oil Racks", "Specification Boards"], clients: ["Continental", "Apollo", "ExxonMobil", "Goodyear"], image: "" }
    ],
    stats_title: "Cross-Industry Impact",
    stats: [
      { value: "6+", label: "Major Industries" },
      { value: "90+", label: "Brand Partners" },
      { value: "1,350+", label: "Projects Delivered" },
      { value: "115+", label: "Cities Covered" }
    ],
    cta_title: "Your Industry, Our Expertise",
    cta_subtitle: "Don't see your industry listed? We create custom POSM solutions for businesses across all sectors.",
    cta_button_text: "Discuss Your Requirements",
    cta_email: "sales@hokico.in"
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-xl">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  const defaultImage = "https://images.unsplash.com/photo-1761244105557-72d8e83ec4d5?w=800&h=800&fit=crop&auto=format";

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }} data-testid="industries-heading">
              {content.hero_title}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-16">
            {content.industries.map((industry, index) => {
              const IconComponent = iconMap[industry.icon] || PackageOpen;
              const imageUrl = industry.image || defaultImage;
              return (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="inline-flex items-center gap-3 mb-6">
                      <IconComponent className="h-12 w-12 text-primary" />
                      <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>{industry.title}</h2>
                    </div>
                    <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                      {industry.description}
                    </p>

                    <div className="space-y-4">
                      {industry.products && industry.products.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-2">Product Solutions</h3>
                          <div className="flex flex-wrap gap-2">
                            {industry.products.map((product, idx) => (
                              <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-sm border border-primary/20">
                                {product}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {industry.clients && industry.clients.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Notable Clients</h3>
                          <div className="flex flex-wrap gap-2">
                            {industry.clients.map((client, idx) => (
                              <span key={idx} className="px-3 py-1 bg-muted/50 text-muted-foreground text-sm rounded-sm border border-border">
                                {client}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <div className="aspect-square rounded-sm overflow-hidden border border-border/50 bg-gray-100">
                      <img
                        src={imageUrl}
                        alt={industry.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {content.stats && content.stats.length > 0 && (
        <section className="py-20 bg-muted/20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>{content.stats_title}</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {content.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>{stat.value}</div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>{content.cta_title}</h2>
          <p className="text-xl mb-8">
            {content.cta_subtitle}
          </p>
          <a href={`mailto:${content.cta_email}`}>
            <button className="px-8 py-3 bg-white text-primary font-semibold rounded-sm hover:bg-gray-100 transition-colors">
              {content.cta_button_text}
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
